import { FeatureFlag, Size } from '@ps/web-core-enums';
import { PromptTourType, useFeature } from '@ps/web-core-store';
import { getWindow } from '@ps/web-core-utils';
import { CashierPath } from '@ps/web-pam-types';
import { Translation, useNavigate, useScreenSize } from '@ps/web-shared-components';
import * as Sentry from '@sentry/react';
import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import CrmZone from '@swf/components/CrmZone/CrmZone';
import OnboardingContainer from '@swf/components/Onboarding/OnboardingContainer/OnboardingContainer';
import { CRMZone } from '@swf/core/enums';
import { getState, select } from '@swf/core/store';
import type { Dispatch } from '@swf/core/store';
import { FirstLoader, Footer, PromptTour, RegulatoryInformation } from '@swf/first';

import { useCashierWidget } from '../../model';
import WalletButton from '../WalletButton/WalletButton';
import WalletButtonGroup from '../WalletButtonGroup/WalletButtonGroup';
import WalletDepositLimitsPanel from '../WalletDepositLimitsPanel/WalletDepositLimitsPanel';
import WalletSummaryPanel from '../WalletSummaryPanel/WalletSummaryPanel';

import AccountStatusAlert from './AccountStatusAlert/AccountStatusAlert';
import CashierTourFinished from './CashierTourFinished/CashierTourFinished';
import CashierTourStep from './CashierTourStep/CashierTourStep';
import {
  TXTCLI_Cashier,
  TXTCLI_Withdraw,
  TXTWCL_Choose_your_preferred_currency_ELL,
  TXTWCL_Deposit,
  TXTWCL_Deposit_limits,
  TXTWCL_Loading_cashier,
  TXTWCL_Make_secure_and_fast_depositsX,
  TXTWCL_Manage_and_view_your_payment_m_ELL,
  TXTWCL_Manage_currencies,
  TXTWCL_Payment_methods,
  TXTWCL_Send_funds_to_other_playersX,
  TXTWCL_Set_your_self_imposed_deposit__ELL,
  TXTWCL_Transaction_history,
  TXTWCL_Transfer_to_player,
  TXTWCL_View_your_depositX_withdrawal__ELL,
  TXTWCL_Withdraw_funds_securelyX
} from './WalletHomeContainer.messages.json';
import styles from './WalletHomeContainer.scss';
import { CashierLocalSettingsPath, cashierOnboardingSteps } from './cashierOnboardingStepsInfo';

const WalletHomeContainer: React.FC = () => {
  const showDepositLimit = useFeature(FeatureFlag.SwcDepositLimitButtonDisplay);
  const showManageCurrencies = useFeature(FeatureFlag.SwcManageCurrencies);
  const showMethodsManagement = useFeature(FeatureFlag.SwcMethodsManagement);
  const showP2PTransfer = useFeature(FeatureFlag.SwcP2pTransfer);
  const navigate = useNavigate();
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const { modal } = useDispatch<Dispatch>();
  const { localSettings } = useDispatch<Dispatch>();
  const { widgetSelect } = useCashierWidget();
  const { needGoLobby, serviceLoading } = useSelector(widgetSelect.get);
  const userId = useSelector(select.account.getUserId);
  const history = useHistory();
  const isEmbedded = useSelector(select.device.isEmbedded);
  const [tourStart, setTourStart] = useState(false);
  const { greaterThan } = useScreenSize();
  const isMobile = useSelector(select.device.isMobile) || !greaterThan(Size.Medium);
  const prevIsAuthenticated = useRef(isAuthenticated);
  const headerRef = useRef<HTMLHeadingElement>(null);

  if (isEmbedded) {
    getWindow()?.starsweb?.app.setBackButton?.(false);
  }

  useEffect(() => {
    if (needGoLobby) {
      if (isEmbedded) {
        const hasSession = !!window.starsweb?.auth.token;

        Sentry.captureException(
          new Error(
            !prevIsAuthenticated.current ? 'Cashier Stuck - No Session' : 'Cashier Stuck - Session'
          ),
          {
            extra: {
              hasSession,
              userId,
              prevIsAuthenticated: prevIsAuthenticated.current
            }
          }
        );
      } else {
        history.push('/'); // go lobby
      }
    }
  }, [needGoLobby, history, isEmbedded, userId]);

  useEffect(() => {
    if (isAuthenticated) {
      if (!select.account.isSWCCashierHome(getState())) {
        navigate('/');
        modal.enqueue({ path: '/account/cashier/home' });
      }
    }
  }, [isAuthenticated, modal, navigate]);

  const handleTourClick = () => {
    setTourStart(true);
    localSettings.set({
      cashier: { cashierTour: { onboardingFinished: false, onboardingModalShown: true } }
    });
  };
  return (
    <>
      <Helmet>{headerRef.current && <title>{headerRef.current.innerText}</title>}</Helmet>
      {serviceLoading || !isAuthenticated ? (
        <FirstLoader label={TXTWCL_Loading_cashier} />
      ) : (
        <>
          <PromptTour handleTourClick={handleTourClick} flow={PromptTourType.Cashier} />
          <OnboardingContainer
            OnboardStep={<CashierTourStep />}
            steps={cashierOnboardingSteps(isMobile)}
            onboardingStoragePath={CashierLocalSettingsPath.Cashier}
            coachMarkClassName={styles.onboardFlowCoachmark}
            arrowClassName={styles.onboardCoachmarkArrow}
            hideOnboarding={false}
          >
            {tourStart && <CashierTourFinished />}
            <div className={styles.wrapper}>
              <div className={styles.walletHome}>
                <span ref={headerRef} hidden={isEmbedded}>
                  <Translation tagName="h2" {...TXTCLI_Cashier} />
                </span>
                <AccountStatusAlert />
                <div className={styles.crm}>
                  <CrmZone
                    zoneId={CRMZone.SWC_CASHIER_MAIN}
                    componentType="Single"
                    dynamicHeight
                    visible
                  />
                </div>
                <div className={styles.container}>
                  <div className={styles.leftContainer}>
                    <WalletSummaryPanel />
                    <WalletDepositLimitsPanel />
                  </div>
                  <WalletButtonGroup>
                    <WalletButton
                      variant="deposit"
                      translation={TXTWCL_Deposit}
                      description={TXTWCL_Make_secure_and_fast_depositsX}
                      onClick={() => navigate(CashierPath.Deposit)}
                    />
                    <WalletButton
                      variant="withdraw"
                      translation={TXTCLI_Withdraw}
                      description={TXTWCL_Withdraw_funds_securelyX}
                      onClick={() => navigate(CashierPath.Withdraw)}
                    />
                    <WalletButton
                      variant="history"
                      translation={TXTWCL_Transaction_history}
                      description={TXTWCL_View_your_depositX_withdrawal__ELL}
                      onClick={() => navigate(CashierPath.TransactionHistory)}
                    />
                    {showManageCurrencies && (
                      <WalletButton
                        variant="currencies"
                        translation={TXTWCL_Manage_currencies}
                        description={TXTWCL_Choose_your_preferred_currency_ELL}
                        onClick={() => navigate(CashierPath.ManageCurrencies)}
                      />
                    )}
                    {showP2PTransfer && (
                      <WalletButton
                        variant="transfer"
                        translation={TXTWCL_Transfer_to_player}
                        description={TXTWCL_Send_funds_to_other_playersX}
                        onClick={() => navigate(CashierPath.P2pTransfer)}
                      />
                    )}
                    {showDepositLimit && (
                      <WalletButton
                        variant="limits"
                        translation={TXTWCL_Deposit_limits}
                        description={TXTWCL_Set_your_self_imposed_deposit__ELL}
                      />
                    )}
                    {showMethodsManagement && (
                      <WalletButton
                        variant="methods"
                        translation={TXTWCL_Payment_methods}
                        description={TXTWCL_Manage_and_view_your_payment_m_ELL}
                        onClick={() => navigate(CashierPath.PaymentMethods)}
                      />
                    )}
                  </WalletButtonGroup>
                </div>
                <RegulatoryInformation />
              </div>
            </div>
          </OnboardingContainer>
          <Footer showFooter={isEmbedded} />
        </>
      )}
    </>
  );
};
export default WalletHomeContainer;
